<script lang="ts" setup>
import { toRefs, computed } from 'vue'
import { useRoute } from 'vue-router'
import AnimatedNumber from '~/components/common/animated-number.vue'
import { useGames } from '~/composables/games/use-games'
import { useJackPot } from '~/composables/common/use-jackpot'
import { useDelay } from '~/composables/common/use-delay'
import { CATEGORY_GAME_SLUG } from '~/resources/lobby-games/urls'
import type { GameItem } from '~composables/lobby-games/interface'

const props = defineProps({
  item: {
    type: Object as () => GameItem,
    default: () => ({ imgSrc: '', title: '', jackpot: false })
  },
  hasSub: { type: Boolean, default: true },
  isMobile: { type: Boolean, default: false },
  hideGameName: { type: Boolean, default: false }
})

const route = useRoute()
const { loading } = useDelay(500)
const { item, hasSub } = toRefs(props)
const { playGame } = useGames()
const { handleJackpotNumber } = useJackPot()

const jackpot = computed(() =>
  route.query.type === CATEGORY_GAME_SLUG.OTHERS ? 0 : handleJackpotNumber(item.value.gameId)
)

const filterProvider = (provider: string): string => {
  if (!provider) {
    return ''
  }
  const providerMappings: Record<string, string> = {
    playngo: "Play'n Go"
  }
  const normalizedProvider = provider.toLowerCase()
  const mappedProvider = providerMappings[normalizedProvider]

  return mappedProvider || normalizedProvider.replace(/-/g, ' ')
}
</script>

<template>
  <div
    :class="['game-item', isMobile ? 'is-mobile' : 'effect-zoomin', item.maintenance ? 'maintained' : '']"
    @click="playGame(item)"
  >
    <NuxtImg
      v-if="item.maintenance"
      class="maintained-img"
      format="webp"
      src="/assets/images/icons/maintained.webp"
      loading="lazy"
      alt="Bảo trì"
    />

    <div class="game-item__thumb effect-zoomin__target banner-thumbnail">
      <div class="thumb-image-wrap ratio">
        <div v-if="isMobile && loading" class="show-loading">
          <NuxtImg
            format="webp"
            src="/assets/images/mobile/home/game-default.png"
            loading="lazy"
            alt="Logo insurance"
            densities="1x"
          />
        </div>
        <NuxtImg
          v-else
          format="webp"
          :src="
            isMobile && item?.img?.mobile?.url
              ? item?.img?.mobile?.url
              : item?.img?.thumbnail?.url ?? item?.img?.cover?.url
          "
          :alt="item?.title"
          layout="fill"
          densities="1x"
          loading="lazy"
          class="game-image"
        />
      </div>

      <div v-if="jackpot !== 0" class="game-item__jackpot">
        <NuxtImg
          class="icon-coin"
          format="webp"
          src="/assets/images/icons/ic-coin-bag.webp"
          loading="lazy"
          alt="coin"
          densities="1x 2x"
        />
        <AnimatedNumber :number="jackpot" />
      </div>
    </div>

    <span v-if="item?.label" class="game-item__badge" :class="item?.label"></span>

    <div v-if="!hideGameName" class="game-item__meta">
      <h3 class="game-item__title text-truncate">{{ item?.title }}</h3>
      <p v-if="hasSub" class="game-item__sub text-truncate">{{ filterProvider(item?.provider || '') }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~assetsComponents/pages/lobby-games/game-item.scss';
</style>
